import {Injectable} from '@angular/core';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {DimensionsService} from '../../services/dimensions.service';
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {DataSetDimensionsGroup} from '../../interfaces/dimensions';
import {
  createDataSetDimensionsGroup,
  createDataSetDimensionsGroupSuccess,
  deleteDataSetDimensionsGroup,
  deleteDataSetDimensionsGroupSuccess,
  initializeDataSetDimensionsGroups,
  initializeDataSetDimensionsGroupsSuccess,
  loadDataSetDimensionsGroups,
  loadDataSetDimensionsGroupsSuccess,
  updateDataSetDimensionsGroup,
  updateDataSetDimensionsGroups,
  updateDataSetDimensionsGroupsSuccess,
  updateDataSetDimensionsGroupSuccess
} from './dataSetDimensionsGroups.actions';
import {selectSite} from '../init/init.selectors';
import {Site} from '../../interfaces/site';
import {filter, map} from 'rxjs/operators';
import {loadGeneralDimensions, loadGeneralDimensionsSuccess} from '../generalDimensions/generalDimensions.actions';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  updateDataSetDimension, updateDataSetDimensions, updateDataSetDimensionsSuccess,
  updateDataSetDimensionSuccess
} from '../dataSetDimensions/dataSetDimensions.actions';
import {selectDataSetDimensionsGroupsState} from './dataSetDimensionsGroups.selectors';
import {selectDataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions.selectors';
import {selectGeneralDimensionsState} from '../generalDimensions/generalDimensions.selectors';
import {DataSetDimensionsGroupsState} from './dataSetDimensionsGroups';
import {DataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions';
import {GeneralDimensionsState} from '../generalDimensions/generalDimensions';
import {DimensionComponentObjects} from '../../classes/dimension-component-object';
import {
  createDataSetConditionalDimension,
  createDataSetConditionalDimensionSuccess,
  deleteDataSetConditionalDimension,
  deleteDataSetConditionalDimensionSuccess,
  loadDataSetConditionalDimensions,
  loadDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimension, updateDataSetConditionalDimensions, updateDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimensionSuccess
} from '../dataSetConditionalDimensions/dataSetConditionalDimensions.actions';
import {selectDataSetConditionalDimensionsState} from '../dataSetConditionalDimensions/dataSetConditionalDimensions.selectors';
import {DataSetConditionalDimensionsState} from '../dataSetConditionalDimensions/dataSetConditionalDimensions';

@Injectable()
export class DataSetDimensionsGroupsEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$: Observable<TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }> => this._actions$.pipe(
    ofType(loadDataSetDimensionsGroups),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetDimensionsGroup>>> => this._dimensionsService.getDataSetDimensionsGroups(dataset)),
    map((response: SerializerResponse<Serializers<DataSetDimensionsGroup>>): TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> } => loadDataSetDimensionsGroupsSuccess({
      groups: response.data
    }))
  ));

  private readonly _updates$: Observable<TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> }> => this._actions$.pipe(
    ofType(updateDataSetDimensionsGroups),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetDimensionsGroup>>> => this._dimensionsService.getDataSetDimensionsGroups(dataset)),
    map((response: SerializerResponse<Serializers<DataSetDimensionsGroup>>): TypedAction<string> & { groups: Serializers<DataSetDimensionsGroup> } => updateDataSetDimensionsGroupsSuccess({
      groups: response.data
    }))
  ));

  private readonly _create$: Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> => this._actions$.pipe(
    ofType(createDataSetDimensionsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Partial<DataSetDimensionsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> => this._dimensionsService.createDataSetDimensionsGroup(dataset, action.group)),
    map((response: SerializerResponse<Serializer<DataSetDimensionsGroup>>): TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> } => createDataSetDimensionsGroupSuccess({
      group: response.data
    }))
  ));

  private readonly _update$: Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> => this._actions$.pipe(
    ofType(updateDataSetDimensionsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Serializer<DataSetDimensionsGroup>, update: Partial<DataSetDimensionsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> => this._dimensionsService.updateDataSetDimensionsGroup(dataset, action.group, action.update)),
    map((response: SerializerResponse<Serializer<DataSetDimensionsGroup>>): TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> } => updateDataSetDimensionsGroupSuccess({
      group: response.data
    }))
  ));

  private readonly _delete$: Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }> => this._actions$.pipe(
    ofType(deleteDataSetDimensionsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetDimensionsGroup>>> => this._dimensionsService.deleteDataSetDimensionsGroup(dataset, action.group)),
    map((response: SerializerResponse<Serializer<DataSetDimensionsGroup>>): TypedAction<string> & { group: Serializer<DataSetDimensionsGroup> } => deleteDataSetDimensionsGroupSuccess({
      group: response.data
    }))
  ));

  // ##########
  // ## INIT ##
  // ##########

  private readonly _init$: Observable<TypedAction<string>> = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadGeneralDimensions,
      loadDataSetDimensions,
      updateDataSetDimensions,
      updateDataSetDimension,
      loadDataSetConditionalDimensions,
      updateDataSetConditionalDimensions,
      createDataSetConditionalDimension,
      updateDataSetConditionalDimension,
      deleteDataSetConditionalDimension,
      loadDataSetDimensionsGroups,
      updateDataSetDimensionsGroups,
      createDataSetDimensionsGroup,
      updateDataSetDimensionsGroup
    ),
    map(() => initializeDataSetDimensionsGroups())
  ));

  private readonly _initSuccess$: Observable<TypedAction<string> & { generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects }> = createEffect((): Observable<TypedAction<string> & { generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects }> => this._actions$.pipe(
    ofType(
      loadGeneralDimensionsSuccess,
      loadDataSetDimensionsSuccess,
      updateDataSetDimensionsSuccess,
      updateDataSetDimensionSuccess,
      loadDataSetConditionalDimensionsSuccess,
      updateDataSetConditionalDimensionsSuccess,
      createDataSetConditionalDimensionSuccess,
      updateDataSetConditionalDimensionSuccess,
      deleteDataSetConditionalDimensionSuccess,
      loadDataSetDimensionsGroupsSuccess,
      updateDataSetDimensionsGroupsSuccess,
      createDataSetDimensionsGroupSuccess,
      updateDataSetDimensionsGroupSuccess
    ),
    withLatestFrom(
      this._store.select(selectDataSetDimensionsGroupsState),
      this._store.select(selectDataSetDimensionsState),
      this._store.select(selectGeneralDimensionsState),
      this._store.select(selectDataSetConditionalDimensionsState)
    ),
    filter(([action, groupsState, datasetDimensionsState, generalDimensionsState, datasetConditionalDimensionsState]: [TypedAction<string>, DataSetDimensionsGroupsState, DataSetDimensionsState, GeneralDimensionsState, DataSetConditionalDimensionsState]): boolean => groupsState.loaded && datasetDimensionsState.loaded && generalDimensionsState.loaded && datasetConditionalDimensionsState.loaded),
    map(([action, groupsState, datasetDimensionsState, generalDimensionsState, datasetConditionalDimensionsState]: [TypedAction<string>, DataSetDimensionsGroupsState, DataSetDimensionsState, GeneralDimensionsState, DataSetConditionalDimensionsState]): TypedAction<string> & { generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects } => initializeDataSetDimensionsGroupsSuccess({
      generalDimensions: generalDimensionsState.componentObjects,
      datasetDimensions: datasetDimensionsState.componentObjects.all,
      datasetConditionalDimensions: datasetConditionalDimensionsState.componentObjects
    }))
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _dimensionsService: DimensionsService
  ) {}

}
